import {XMarkIcon} from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import DynamicEditForm from '../DynamicEditForm.jsx';
import LoadingSpinner from '../LoadingSpinner.jsx';
import ModalError from '../Modal/ModalError.jsx';
import ModalLoading from '../Modal/ModalLoading.jsx';
import useParseCSV from '/src/hooks/useParseCSV.js';
import useQueryEffect from '/src/hooks/useQueryEffect.js';
import useStringifyCSV from '/src/hooks/useStringifyCSV.js';
import { useExportPlotsMutation, useImportPlotsMutation } from '/src/redux/importExportApi.js';
import { stringifyDate } from '/src/utils/dateInput.js';
import { downloadFile } from '/src/utils/downloadFile.js';

const formConfig = {
    autoFocus: false,
    saveText: 'Export',
    hideDiscard: true,
    hideRequiredHint: true,
    fields: [],
};

const headerMap = {
    section: 'Section',
    lot: 'Lot Num',
    lotPart: 'Lot Letter',
    firstName: 'First Name',
    lastName: 'Last Name',
    suffix: 'Suffix',
    notes: 'Notes',
    sizeNotes: 'Size Notes',
    sqf: 'SQF',
    price: 'Price',
    date: 'Date',
}

export default function MapDataExport({onClose}){
    const [error, setError] = useState(null);
    const [parsedResult, setParsedResult] = useState(null);
    const [download, { error: downloadError, isLoading: isDownloading, isSuccess: isDownloadSuccess, data: downloadResult }] = useExportPlotsMutation();
    // btn press --> download --> format --> stringify --> download file

    const userTookAction = useQueryEffect(error=>{
        if(error){
            setError(error);
        }
    },[isDownloadSuccess],[downloadError]);

    useEffect(()=>{
        if(downloadResult){
            try {
                const outItems = parseData(downloadResult);
                setParsedResult(outItems);
            } catch (error) {
                console.error('error parsing export data', error);
                setError(error);  
            }
        }
    },[downloadResult]);

    const csvResult = useStringifyCSV(parsedResult);

    function parseData(allPlots){
        const out = [];
        // add headings
        const header = itemToArr(headerMap);
        out.push(header);
        // go through each plot
        for(let plot of allPlots){ 
            // separate plotOwners
            const plotOwners = plot.plotOwners;
            delete plot.plotOwners;
            if(plotOwners && plotOwners.length){
                // make a new line for each owner
                for(let owner of plotOwners){
                    out.push(itemToArr({
                        ...plot,
                        ...owner,
                    }));
                }
            }else{
                // or just push this plot as a single line
                out.push(itemToArr(plot));
            }
        }
        return out;
    }

    function itemToArr(item){
        const out = [];
        for(let key of Object.keys(headerMap)){
            if(headerMap.hasOwnProperty(key)){
                const value = item && ((item[key] === 0)? '0':item[key]) && (item[key] + '') || '';
                out.push(value);
            }
        }
        return out;
    }

    function reset(){
        setParsedResult(null);
        setError(null);
    }

    function onExportBtn(){
        userTookAction();
        download();
    }
    function onDownloadBtn(){
        const now = new Date();
        const dateStr = stringifyDate({
            month: now.getMonth() + 1,
            day: now.getDate(),
            year: now.getFullYear(),
        })
        downloadFile(`export-${dateStr}.csv`, csvResult, 'text/csv');
        reset();
        if(onClose) onClose();
    }

    return (
        <div>
            {error && <ModalError title="Error" message={error.message} description={error.status || error.code} callback={()=>setError(null)} />}
            <h4 className='text-xl m-4 font-light'>Data export</h4>
            <div className='py-3 px-4'>

            {(!!csvResult && (
                <div>
                    <p className='text-xl mb-4'>Export Ready</p>
                    <div className='flex group'>
                        <button className='btn flex-auto group-hover:btn-invert' onClick={()=>onDownloadBtn()}>Save File</button>
                        <button className='btn-invert' onClick={()=>reset()}>Cancel</button>
                    </div>
                </div>
            )) || ((isDownloading || (parsedResult && !csvResult)) && (
                <button disabled={true} className='btn w-full font-bold p-4 shadow-lg uppercase text-lg flex'>
                    <span>{isDownloading?'Downloading':'Exporting'}...</span>
                    <LoadingSpinner className='w-6' />
                </button>
            )) || (
                <button onClick={()=>onExportBtn()} className='btn w-full font-bold p-4 shadow-lg uppercase text-lg'>Export</button>                
            )}
            </div>
        </div>
    );
}